import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  ArrowLeft, User, Wallet, Gift, TrendingUp, TrendingDown, ArrowDownCircle,
  ArrowUpCircle, Gamepad2, Clock, Calendar, Loader2, Trophy, Target,
  CheckCircle2, XCircle, DollarSign,
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts';

const PERIODS = [
  { id: 'day', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'week', label: '7 Days' },
  { id: 'month', label: '30 Days' },
  { id: '90d', label: '90 Days' },
  { id: 'all', label: 'All Time' },
];

const fmtUSD = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function PlayerPortfolioPage({ playerId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [rangeActive, setRangeActive] = useState(false);

  const fetchPortfolio = async () => {
    setLoading(true);
    try {
      let url = `/api/admin/players/${playerId}/portfolio?period=${period}`;
      if (rangeActive && customRange.start && customRange.end) {
        url = `/api/admin/players/${playerId}/portfolio?start=${encodeURIComponent(customRange.start)}&end=${encodeURIComponent(customRange.end)}`;
      }
      const res = await api.get(url);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load portfolio');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPortfolio(); /* eslint-disable-next-line */ }, [period, rangeActive, playerId]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="portfolio-loading">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const p = data.player || {};
  const L = data.lifetime || {};
  const K = data.period_kpis || {};
  const gran = data.granularity || 'day';

  const depSeries = (data.timeseries?.deposits || []).map(r => ({ ts: r.ts, Deposits: r.amount, _count: r.count }));
  const wdSeries = (data.timeseries?.withdrawals || []).map(r => ({ ts: r.ts, Withdrawals: r.amount, _count: r.count }));
  const mergedPay = mergeSeries(depSeries, wdSeries, 'Deposits', 'Withdrawals');

  const gameSeries = mergeSeries(
    (data.timeseries?.game_loads || []).map(r => ({ ts: r.ts, Loads: r.amount })),
    (data.timeseries?.game_redeems || []).map(r => ({ ts: r.ts, Redeems: r.amount })),
    'Loads', 'Redeems'
  );

  return (
    <div className="space-y-5" data-testid="player-portfolio-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg bg-[#0B0E14] border border-white/10 text-gray-400 hover:text-white transition"
            data-testid="portfolio-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#8b5cf6] flex items-center justify-center flex-shrink-0">
            <User className="w-6 h-6 text-black" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">{p.username || 'Player'}</h2>
            <p className="text-xs text-gray-400">
              {p.email || '—'} · joined {fmtDate(p.created_at)} ·{' '}
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.disabled ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
                {p.disabled ? 'Disabled' : 'Active'}
              </span>{' '}
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] ml-1">
                {(p.tier || 'starter').toUpperCase()}
              </span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-xl bg-[#0B0E14] border border-white/10 p-1" data-testid="portfolio-period-filter">
            {PERIODS.map((pp) => (
              <button
                key={pp.id}
                onClick={() => { setPeriod(pp.id); setRangeActive(false); setCustomRange({ start: '', end: '' }); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                  !rangeActive && period === pp.id ? 'bg-[#D4AF37] text-black' : 'text-gray-400 hover:text-white'
                }`}
                data-testid={`portfolio-period-${pp.id}`}
              >
                {pp.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowRangePicker((v) => !v)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 border transition ${
              rangeActive ? 'bg-[#D4AF37] text-black border-[#D4AF37]' : 'bg-[#0B0E14] text-gray-400 border-white/10 hover:text-white'
            }`}
            data-testid="portfolio-date-range-btn"
          >
            <Calendar className="w-3.5 h-3.5" />
            {rangeActive && customRange.start
              ? `${customRange.start.slice(0,10)} → ${customRange.end.slice(0,10)}`
              : 'Custom range'}
          </button>
        </div>
      </div>

      {showRangePicker && (
        <div
          className="rounded-2xl p-4 bg-[#131822] border border-[#D4AF37]/30 flex items-end gap-3 flex-wrap"
          data-testid="portfolio-date-range-picker"
        >
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">Start date</label>
            <input
              type="date"
              value={customRange.start ? customRange.start.slice(0, 10) : ''}
              onChange={(e) => setCustomRange((r) => ({ ...r, start: e.target.value ? `${e.target.value}T00:00:00.000Z` : '' }))}
              className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              data-testid="portfolio-date-start"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-500 block mb-1">End date</label>
            <input
              type="date"
              value={customRange.end ? customRange.end.slice(0, 10) : ''}
              onChange={(e) => setCustomRange((r) => ({ ...r, end: e.target.value ? `${e.target.value}T23:59:59.999Z` : '' }))}
              className="bg-[#0B0E14] border border-white/10 rounded-lg px-3 py-1.5 text-sm text-white"
              data-testid="portfolio-date-end"
            />
          </div>
          <button
            onClick={() => { if (customRange.start && customRange.end) { setRangeActive(true); setShowRangePicker(false); } }}
            disabled={!customRange.start || !customRange.end}
            className="px-4 py-1.5 rounded-lg bg-[#D4AF37] text-black text-xs font-semibold hover:bg-[#F5D76E] disabled:opacity-40 transition"
            data-testid="portfolio-date-apply"
          >
            Apply Range
          </button>
        </div>
      )}

      {/* Lifetime strip */}
      <div
        className="rounded-2xl p-5 bg-gradient-to-br from-[#1a0f2e] to-[#0B0E14] border border-white/10"
        data-testid="portfolio-lifetime"
      >
        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#D4AF37]" /> Lifetime (all time)
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={Wallet} color="#22c55e" label="Current Wallet"
            primary={fmtUSD(data.wallet?.cash_balance)}
            secondary={`+${fmtInt(data.wallet?.play_credits)} play credits`}
            testid="stat-wallet" />
          <Stat icon={ArrowDownCircle} color="#22c55e" label="Total Deposits"
            primary={fmtUSD(L.deposit_amount)}
            secondary={`${fmtInt(L.deposit_count)} txns`}
            testid="stat-lifetime-deposits" />
          <Stat icon={ArrowUpCircle} color="#ef4444" label="Total Withdrawals"
            primary={fmtUSD(L.withdraw_amount)}
            secondary={`${fmtInt(L.withdraw_count)} txns`}
            testid="stat-lifetime-withdrawals" />
          <Stat icon={L.net_revenue >= 0 ? TrendingUp : TrendingDown}
            color={L.net_revenue >= 0 ? '#22c55e' : '#ef4444'}
            label="Net Revenue"
            primary={fmtUSD(L.net_revenue)}
            secondary="lifetime"
            testid="stat-lifetime-net" />
          <Stat icon={Gift} color="#D4AF37" label="Bonuses Received"
            primary={fmtUSD(L.bonus_amount)}
            secondary={`${fmtInt(L.bonus_count)} events`}
            testid="stat-lifetime-bonuses" />
          <Stat icon={Gamepad2} color="#8b5cf6" label="Games Played"
            primary={fmtInt(L.games_played)}
            secondary={`${fmtUSD(L.total_load_amount)} loaded`}
            testid="stat-lifetime-games" />
          <Stat icon={Target} color="#3b82f6" label="Pending Deposits"
            primary={`${fmtInt(L.pending_deposit_count)}`}
            secondary={fmtUSD(L.pending_deposit_amount)}
            testid="stat-pending-dep" />
          <Stat icon={Target} color="#f59e0b" label="Pending Withdrawals"
            primary={`${fmtInt(L.pending_withdraw_count)}`}
            secondary={fmtUSD(L.pending_withdraw_amount)}
            testid="stat-pending-wd" />
        </div>
      </div>

      {/* Period KPIs */}
      <div data-testid="portfolio-period">
        <h3 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#D4AF37]" />
          Period: {rangeActive ? 'Custom range' : PERIODS.find(x => x.id === period)?.label}
          <span className="text-[11px] text-gray-500 font-normal">
            ({(data.start_date || '').slice(0, 10)} → {(data.end_date || '').slice(0, 10)})
          </span>
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat icon={ArrowDownCircle} color="#22c55e" label="Deposits"
            primary={fmtUSD(K.deposit_amount)} secondary={`${fmtInt(K.deposit_count)} txns`}
            testid="stat-period-deposits" />
          <Stat icon={ArrowUpCircle} color="#ef4444" label="Withdrawals"
            primary={fmtUSD(K.withdraw_amount)} secondary={`${fmtInt(K.withdraw_count)} txns`}
            testid="stat-period-withdrawals" />
          <Stat icon={K.net_revenue >= 0 ? TrendingUp : TrendingDown}
            color={K.net_revenue >= 0 ? '#22c55e' : '#ef4444'} label="Net"
            primary={fmtUSD(K.net_revenue)} secondary="in period"
            testid="stat-period-net" />
          <Stat icon={Gamepad2} color="#8b5cf6" label="Games Played"
            primary={fmtInt(K.games_played_in_period)} secondary={`${fmtInt(K.bonus_count)} bonuses`}
            testid="stat-period-games" />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="chart-money-flow">
          <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-400" /> Money flow (period)
          </h4>
          {mergedPay.length === 0 ? (
            <Empty label="No deposits or withdrawals in this period" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={mergedPay}>
                <defs>
                  <linearGradient id="gD" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22c55e" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gW" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip contentStyle={tip} formatter={(v) => fmtUSD(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Area type="monotone" dataKey="Deposits" stroke="#22c55e" fill="url(#gD)" strokeWidth={2} />
                <Area type="monotone" dataKey="Withdrawals" stroke="#ef4444" fill="url(#gW)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="chart-game-flow">
          <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
            <Gamepad2 className="w-4 h-4 text-[#8b5cf6]" /> Game activity (period)
          </h4>
          {gameSeries.length === 0 ? (
            <Empty label="No game activity in this period" />
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={gameSeries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                <XAxis dataKey="ts" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 10 }} />
                <Tooltip contentStyle={tip} formatter={(v) => fmtUSD(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="Loads" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Redeems" fill="#22c55e" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Game breakdown lifetime */}
      <div className="rounded-2xl p-5 bg-[#131822] border border-white/10" data-testid="game-breakdown-lifetime">
        <h4 className="text-sm font-semibold text-gray-300 mb-3 flex items-center gap-2">
          <Gamepad2 className="w-4 h-4 text-[#8b5cf6]" /> Lifetime game spend
        </h4>
        {(data.game_breakdown_lifetime || []).length === 0 ? (
          <Empty label="This player hasn't played any games yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-gray-500 border-b border-white/5">
                  <th className="py-2 pr-4">Game</th>
                  <th className="py-2 pr-4 text-right">Loads</th>
                  <th className="py-2 pr-4 text-right">Loaded ($)</th>
                  <th className="py-2 pr-4 text-right">Redeems</th>
                  <th className="py-2 pr-4 text-right">Redeemed ($)</th>
                  <th className="py-2 pr-4 text-right">Net spend</th>
                  <th className="py-2 text-right">Last played</th>
                </tr>
              </thead>
              <tbody>
                {(data.game_breakdown_lifetime || []).map((g) => (
                  <tr
                    key={g.game_id}
                    className="border-b border-white/5 hover:bg-white/[0.02]"
                    data-testid={`game-row-${g.game_id}`}
                  >
                    <td className="py-2.5 pr-4 font-medium text-white capitalize">{g.game_name || g.game_id}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums text-gray-300">{fmtInt(g.load_count)}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums text-green-400">{fmtUSD(g.load_amount)}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums text-gray-300">{fmtInt(g.redeem_count)}</td>
                    <td className="py-2.5 pr-4 text-right tabular-nums text-[#D4AF37]">{fmtUSD(g.redeem_amount)}</td>
                    <td className={`py-2.5 pr-4 text-right tabular-nums font-semibold ${g.net_spend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {fmtUSD(g.net_spend)}
                    </td>
                    <td className="py-2.5 text-right text-[11px] text-gray-500">{fmtDate(g.last_played_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent transactions 3-column */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TxnList
          title="Recent Deposits"
          icon={ArrowDownCircle}
          color="#22c55e"
          fmt={fmtUSD}
          rows={(data.recent_transactions?.deposits || []).map(r => ({
            id: r.id, amount: r.charged_amount || r.amount, status: r.status,
            label: r.gateway_provider || r.payment_method || 'manual', created_at: r.created_at,
          }))}
          testid="recent-deposits"
        />
        <TxnList
          title="Recent Withdrawals"
          icon={ArrowUpCircle}
          color="#ef4444"
          fmt={fmtUSD}
          rows={(data.recent_transactions?.withdrawals || []).map(r => ({
            id: r.id, amount: r.amount, status: r.status,
            label: r.payment_method || 'manual', created_at: r.created_at,
          }))}
          testid="recent-withdrawals"
        />
        <TxnList
          title="Recent Game Transactions"
          icon={Gamepad2}
          color="#8b5cf6"
          fmt={fmtUSD}
          rows={(data.recent_transactions?.game_transactions || []).map(r => ({
            id: r.id,
            amount: r.type === 'game_load' ? r.gross_amount : r.net_amount,
            status: r.type,
            label: r.game_name || r.game_id,
            created_at: r.created_at,
          }))}
          testid="recent-games"
        />
      </div>
    </div>
  );
}

const tip = {
  background: '#0B0E14',
  border: '1px solid #ffffff15',
  borderRadius: 8,
  fontSize: 12,
};

function mergeSeries(a, b, keyA, keyB) {
  const map = new Map();
  (a || []).forEach((r) => map.set(r.ts, { ts: r.ts, [keyA]: r[keyA] || 0 }));
  (b || []).forEach((r) => {
    const cur = map.get(r.ts) || { ts: r.ts, [keyA]: 0 };
    cur[keyB] = r[keyB] || 0;
    map.set(r.ts, cur);
  });
  return Array.from(map.values()).sort((x, y) => (x.ts || '').localeCompare(y.ts || ''));
}

function Stat({ icon: Icon, color, label, primary, secondary, testid }) {
  return (
    <div
      className="rounded-xl p-3 bg-white/[0.02] border border-white/5 flex items-start gap-3"
      data-testid={testid}
    >
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}18` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <p className="text-xl font-bold tabular-nums text-white truncate">{primary}</p>
        {secondary && <p className="text-[10px] text-gray-500 truncate">{secondary}</p>}
      </div>
    </div>
  );
}

function Empty({ label }) {
  return (
    <div className="flex items-center justify-center h-32 text-xs text-gray-500 border border-dashed border-white/10 rounded-xl">
      {label || 'No data'}
    </div>
  );
}

function TxnList({ title, icon: Icon, color, rows, testid, fmt = fmtUSD }) {
  return (
    <div className="rounded-2xl p-4 bg-[#131822] border border-white/10" data-testid={testid}>
      <h4 className="text-xs font-semibold text-gray-300 mb-3 flex items-center gap-2">
        <Icon className="w-4 h-4" style={{ color }} /> {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-[11px] text-gray-500 italic">No recent activity in this period.</p>
      ) : (
        <div className="space-y-1.5 max-h-80 overflow-y-auto pr-1">
          {rows.map((r) => (
            <div
              key={r.id}
              className="rounded-lg bg-white/[0.02] border border-white/5 px-2.5 py-2 text-[11px]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-white font-semibold tabular-nums">{fmt(r.amount)}</span>
                <StatusPill status={r.status} />
              </div>
              <p className="text-gray-400 truncate">{r.label}</p>
              <p className="text-gray-600 text-[10px] flex items-center gap-1">
                <Clock className="w-3 h-3" /> {fmtDate(r.created_at)}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const s = (status || '').toLowerCase();
  if (['approved', 'paid', 'completed', 'game_redeem'].includes(s)) {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-500/20 text-green-300 flex items-center gap-1">
        <CheckCircle2 className="w-3 h-3" /> {status}
      </span>
    );
  }
  if (['failed', 'rejected', 'cancelled'].includes(s)) {
    return (
      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-500/20 text-red-300 flex items-center gap-1">
        <XCircle className="w-3 h-3" /> {status}
      </span>
    );
  }
  return (
    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-500/20 text-gray-300">{status}</span>
  );
}
